import React, { useState, useMemo } from 'react';
import { Plus, Search, Settings, User, MessageSquare, AlertCircle, FileText, MessageCircle, StickyNote, BookOpen, ClipboardList, Lightbulb, Briefcase, Calendar, LogOut, Users, Bot, X, Edit2, Save, BarChart3 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { NoteDetailModal } from './NoteDetailModal';
import { cn } from '../lib/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Conversation } from '../types/conversation';
import { PrivateNote } from '../types/privateNote';
import { ShareableEntity } from '../types/privateNote';
import { useSystemNotes } from '../context/SystemNotesContext';
import { SystemNote } from '../types/systemNote';
import { Brain, Trash2, Sparkles } from 'lucide-react';

export interface Chat {
    id: string;
    title: string;
    updatedAt: Date;
}

interface ChatSidebarProps {
    chats: Chat[];
    activeChatId: string | null;
    onChatSelect: (chatId: string) => void;
    onNewChat: () => void;
    conversations?: Conversation[];
    activeConversationId?: string | null;
    onConversationSelect?: (conversationId: string) => void;
    onPersonSelect?: (personName: string) => void;
    activePersonName?: string | null;
    onProfileClick?: () => void;
    className?: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
    chats,
    activeChatId,
    onChatSelect,
    onNewChat,
    conversations = [],
    activeConversationId,
    onConversationSelect,
    onPersonSelect,
    activePersonName,
    onProfileClick,
    className,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedNote, setSelectedNote] = useState<PrivateNote | null>(null);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedMyNote, setSelectedMyNote] = useState<SystemNote | null>(null);
    const [isMyNoteModalOpen, setIsMyNoteModalOpen] = useState(false);
    const [isEditingMemory, setIsEditingMemory] = useState(false);
    const [memorySharedWith, setMemorySharedWith] = useState<{ people: string[]; groups: string[]; bots: string[] }>({
        people: [],
        groups: [],
        bots: [],
    });
    const [showAddMemoryPeople, setShowAddMemoryPeople] = useState(false);
    const [showAddMemoryGroups, setShowAddMemoryGroups] = useState(false);
    const [showAddMemoryBots, setShowAddMemoryBots] = useState(false);
    const { systemNotes, deleteSystemNote } = useSystemNotes();
    const { logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    // Dummy private notes data
    const [privateNotes, setPrivateNotes] = useState<PrivateNote[]>([
        {
            id: 'note-1',
            title: 'Project Planning Notes',
            content: 'Key points for the upcoming project:\n- Budget allocation\n- Timeline considerations\n- Resource requirements\n- Risk assessment',
            author: 'Current User',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
            updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
            sharedWith: {
                people: ['person-1', 'person-2'],
                groups: ['group-1'],
                bots: [],
            },
        },
        {
            id: 'note-2',
            title: 'Meeting Minutes - Q4 Review',
            content: 'Discussion points:\n1. Performance metrics\n2. Team feedback\n3. Next quarter goals',
            author: 'Current User',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
            updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3),
            sharedWith: {
                people: ['person-3'],
                groups: ['group-2'],
                bots: ['assistant-1'],
            },
        },
        {
            id: 'note-3',
            title: 'Technical Architecture Ideas',
            content: 'Exploring new architecture patterns:\n- Microservices approach\n- API gateway design\n- Database sharding strategy',
            author: 'Current User',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
            updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6),
            sharedWith: {
                people: [],
                groups: [],
                bots: ['assistant-2'],
            },
        },
        {
            id: 'note-4',
            title: 'Personal Reminders',
            content: 'Things to remember:\n- Follow up with client\n- Review proposal\n- Schedule team meeting',
            author: 'Current User',
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12),
            updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
            sharedWith: {
                people: [],
                groups: [],
                bots: [],
            },
        },
    ]);

    // Dummy data for available entities
    const availablePeople: ShareableEntity[] = [
        { id: 'person-1', name: 'John Doe', type: 'person' },
        { id: 'person-2', name: 'Jane Smith', type: 'person' },
        { id: 'person-3', name: 'Mike Johnson', type: 'person' },
        { id: 'person-4', name: 'Sarah Williams', type: 'person' },
        { id: 'person-5', name: 'David Brown', type: 'person' },
    ];

    const availableGroups: ShareableEntity[] = [
        { id: 'group-1', name: 'Engineering Team', type: 'group' },
        { id: 'group-2', name: 'Product Managers', type: 'group' },
        { id: 'group-3', name: 'Design Team', type: 'group' },
        { id: 'group-4', name: 'Leadership', type: 'group' },
    ];

    const availableBots: ShareableEntity[] = [
        { id: 'assistant-1', name: "John's Assistant", type: 'assistant' },
        { id: 'assistant-2', name: "Jane's Assistant", type: 'assistant' },
        { id: 'assistant-3', name: "Mike's Assistant", type: 'assistant' },
        { id: 'assistant-4', name: "Sarah's Assistant", type: 'assistant' },
    ];

    const filteredChats = useMemo(() => {
        if (!searchQuery.trim()) return chats;
        const query = searchQuery.toLowerCase();
        return chats.filter(chat =>
            chat.title.toLowerCase().includes(query)
        );
    }, [chats, searchQuery]);

    const filteredConversations = useMemo(() => {
        if (!searchQuery.trim()) return conversations;
        const query = searchQuery.toLowerCase();
        return conversations.filter(conv =>
            conv.title.toLowerCase().includes(query) ||
            conv.question.toLowerCase().includes(query)
        );
    }, [conversations, searchQuery]);

    // Combine and filter all notes (private + system notes)
    const allNotes = useMemo(() => {
        const combined = [
            ...privateNotes.map(note => ({ ...note, type: 'private' as const })),
            ...systemNotes.map(note => ({ ...note, type: 'system' as const }))
        ];

        if (!searchQuery.trim()) return combined;

        const query = searchQuery.toLowerCase();
        return combined.filter(item => {
            if (item.type === 'private') {
                return item.title.toLowerCase().includes(query) ||
                    item.content.toLowerCase().includes(query);
            } else {
                return item.title.toLowerCase().includes(query) ||
                    item.summary.toLowerCase().includes(query) ||
                    item.question.toLowerCase().includes(query);
            }
        });
    }, [privateNotes, systemNotes, searchQuery]);

    // Sort by updatedAt (most recent first)
    const sortedAllNotes = useMemo(() => {
        return [...allNotes].sort((a, b) => {
            const dateA = a.type === 'private' ? a.updatedAt : a.updatedAt;
            const dateB = b.type === 'private' ? b.updatedAt : b.updatedAt;
            return dateB.getTime() - dateA.getTime();
        });
    }, [allNotes]);

    const handleNoteClick = (note: PrivateNote) => {
        setSelectedNote(note);
        setIsNoteModalOpen(true);
    };

    const handleNoteSave = (updatedNote: PrivateNote) => {
        setPrivateNotes(prev => prev.map(note =>
            note.id === updatedNote.id ? updatedNote : note
        ));
    };

    const handleNoteModalClose = () => {
        setIsNoteModalOpen(false);
        setSelectedNote(null);
    };

    const pendingReviewCount = useMemo(() => {
        return conversations.filter(c => c.reviewRequested && c.reviewStatus === 'pending').length;
    }, [conversations]);

    const formatDate = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    const truncateMemoryName = (name: string, maxLength: number = 30): string => {
        if (name.length <= maxLength) return name;
        return name.substring(0, maxLength) + '...';
    };

    const getNoteIcon = (title: string) => {
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes('meeting') || lowerTitle.includes('minutes')) {
            return Calendar;
        }
        if (lowerTitle.includes('project') || lowerTitle.includes('planning')) {
            return Briefcase;
        }
        if (lowerTitle.includes('technical') || lowerTitle.includes('architecture') || lowerTitle.includes('ideas')) {
            return Lightbulb;
        }
        if (lowerTitle.includes('reminder') || lowerTitle.includes('personal')) {
            return ClipboardList;
        }
        if (lowerTitle.includes('review') || lowerTitle.includes('notes')) {
            return BookOpen;
        }
        return StickyNote; // Default icon
    };

    const formatDateKey = (date: Date): string => {
        // Returns a date key like "2024-01-15" for grouping
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    const formatDateHeader = (dateKey: string): string => {
        // Format date key for display
        const date = new Date(dateKey);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const dateStr = date.toISOString().split('T')[0];
        const todayStr = today.toISOString().split('T')[0];
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        if (dateStr === todayStr) return 'Today';
        if (dateStr === yesterdayStr) return 'Yesterday';

        // Check if within last 7 days
        const diff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
        if (diff < 7) return `${diff} days ago`;

        // Otherwise return formatted date
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
    };

    // Group conversations by username, then by date
    const groupedConversations = useMemo(() => {
        if (!filteredConversations.length) return {};

        const grouped: Record<string, Record<string, Conversation[]>> = {};

        filteredConversations.forEach(conv => {
            const username = conv.askedBy || 'Unknown';
            const dateKey = formatDateKey(conv.answeredAt);

            if (!grouped[username]) {
                grouped[username] = {};
            }
            if (!grouped[username][dateKey]) {
                grouped[username][dateKey] = [];
            }
            grouped[username][dateKey].push(conv);
        });

        // Sort conversations within each date group by time (newest first)
        Object.keys(grouped).forEach(username => {
            Object.keys(grouped[username]).forEach(dateKey => {
                grouped[username][dateKey].sort((a, b) =>
                    b.answeredAt.getTime() - a.answeredAt.getTime()
                );
            });
        });

        return grouped;
    }, [filteredConversations]);

    // Get sorted usernames (most recent first)
    const sortedUsernames = useMemo(() => {
        return Object.keys(groupedConversations).sort((a, b) => {
            // Get most recent conversation date for each username
            const aDates = Object.keys(groupedConversations[a]);
            const bDates = Object.keys(groupedConversations[b]);
            const aMostRecent = aDates.length > 0 ? Math.max(...aDates.map(d => new Date(d).getTime())) : 0;
            const bMostRecent = bDates.length > 0 ? Math.max(...bDates.map(d => new Date(d).getTime())) : 0;
            return bMostRecent - aMostRecent;
        });
    }, [groupedConversations]);

    return (
        <div className={cn("flex flex-col h-full bg-background/50 border-r border-border/50", className)}>
            {/* Header */}
            <div className="px-5 pt-6 pb-4 border-b border-border/50">
                <h1 className="text-lg font-bold text-foreground mb-4 tracking-tight">
                    Woddy Assistant
                </h1>
                <Button
                    onClick={onNewChat}
                    className="w-full"
                    size="default"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    New chat
                </Button>
            </div>

            {/* Search */}
            <div className="px-5 py-4 border-b border-border/50">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 bg-muted/50 border-border/50 focus-visible:ring-1"
                    />
                </div>
            </div>

            {/* Chat List and Conversations List */}
            <ScrollArea className="flex-1">
                <div className="px-3 py-2">
                    <Accordion type="single" collapsible className="w-full" defaultValue="chats">
                        {/* Chats Section */}
                        <AccordionItem value="chats" className="border-none">
                            <AccordionTrigger className="px-2 py-2.5 hover:no-underline rounded-md hover:bg-muted/50 transition-colors">
                                <h2 className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-2">
                                    <MessageCircle className="h-3.5 w-3.5" />
                                    Chats
                                </h2>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pt-1 pb-2">
                                {filteredChats.length === 0 ? (
                                    <div className="text-center text-xs text-muted-foreground py-6">
                                        {searchQuery ? 'No chats found' : 'No chats yet'}
                                    </div>
                                ) : (
                                    <div className="space-y-0.5">
                                        {filteredChats.map((chat) => (
                                            <button
                                                key={chat.id}
                                                onClick={() => onChatSelect(chat.id)}
                                                className={cn(
                                                    "w-full text-left px-3 py-2 rounded-lg transition-all",
                                                    "hover:bg-accent/70 hover:text-accent-foreground",
                                                    "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1",
                                                    activeChatId === chat.id && "bg-accent text-accent-foreground shadow-sm"
                                                )}
                                            >
                                                <div className="truncate font-semibold text-sm text-foreground" title={chat.title}>
                                                    {chat.title.length > 30 ? `${chat.title.substring(0, 30)}...` : chat.title}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {formatDate(chat.updatedAt)}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Conversations Section */}
                        <AccordionItem value="interactions" className="border-none">
                            <AccordionTrigger className="px-2 py-2.5 hover:no-underline rounded-md hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-2 flex-1">
                                    <h2 className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-2">
                                        <MessageSquare className="h-3.5 w-3.5" />
                                        Reviews
                                    </h2>
                                    {pendingReviewCount > 0 && (
                                        <Badge variant="destructive" className="h-4.5 px-1.5 text-[10px] font-semibold">
                                            {pendingReviewCount}
                                        </Badge>
                                    )}
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pt-1 pb-2">
                                {sortedUsernames.length === 0 ? (
                                    <div className="text-center text-xs text-muted-foreground py-6">
                                        {searchQuery ? 'No conversations found' : 'No conversations yet'}
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {sortedUsernames.map((username) => {
                                            const userConversations = groupedConversations[username];
                                            const allConversations = Object.values(userConversations).flat();
                                            const hasPendingReview = allConversations.some(
                                                conv => conv.reviewRequested && conv.reviewStatus === 'pending'
                                            );
                                            const mostRecentDate = allConversations.length > 0
                                                ? formatDate(allConversations.sort((a, b) => b.answeredAt.getTime() - a.answeredAt.getTime())[0].answeredAt)
                                                : '';

                                            return (
                                                <button
                                                    key={username}
                                                    onClick={() => onPersonSelect?.(username)}
                                                    className={cn(
                                                        "w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
                                                        "hover:bg-accent/70 hover:text-accent-foreground",
                                                        "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1",
                                                        activePersonName === username && "bg-accent text-accent-foreground shadow-sm"
                                                    )}
                                                >
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                        <User className="h-4 w-4 text-primary" />
                                                    </div>
                                                    <div className="flex-1 min-w-0 text-left">
                                                        <div className="flex items-center gap-2">
                                                            <div className="truncate font-semibold text-sm text-foreground">
                                                                {username}
                                                            </div>
                                                            {hasPendingReview && (
                                                                <AlertCircle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground mt-0.5">
                                                            {mostRecentDate}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Preferences Section (Combined Private + System Notes) */}
                        <AccordionItem value="notes" className="border-none">
                            <AccordionTrigger className="px-2 py-2.5 hover:no-underline rounded-md hover:bg-muted/50 transition-colors">
                                <h2 className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5" />
                                    Memory
                                </h2>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pt-1 pb-2">
                                {sortedAllNotes.length === 0 ? (
                                    <div className="text-center text-xs text-muted-foreground py-6">
                                        {searchQuery ? 'No notes found' : 'No notes yet'}
                                    </div>
                                ) : (
                                    <div className="space-y-0.5">
                                        {sortedAllNotes.map((item) => {
                                            if (item.type === 'private') {
                                                const NoteIcon = getNoteIcon(item.title);
                                                return (
                                                    <button
                                                        key={item.id}
                                                        onClick={() => handleNoteClick(item)}
                                                        className={cn(
                                                            "w-full text-left px-3 py-2 rounded-lg transition-all",
                                                            "hover:bg-accent/70 hover:text-accent-foreground",
                                                            "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-shrink-0">
                                                                <NoteIcon className="h-4 w-4 text-muted-foreground" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="truncate font-semibold text-sm text-foreground" title={item.title}>
                                                                    {truncateMemoryName(item.title)}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                                    {formatDate(item.updatedAt)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            } else {
                                                return (
                                                    <button
                                                        key={item.id}
                                                        onClick={() => {
                                                            setSelectedMyNote(item);
                                                            setMemorySharedWith({
                                                                people: item.sharedWith?.people || [],
                                                                groups: item.sharedWith?.groups || [],
                                                                bots: item.sharedWith?.bots || [],
                                                            });
                                                            setIsEditingMemory(false);
                                                            setIsMyNoteModalOpen(true);
                                                        }}
                                                        className={cn(
                                                            "w-full text-left px-3 py-2 rounded-lg transition-all",
                                                            "hover:bg-accent/70 hover:text-accent-foreground",
                                                            "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div className="flex-shrink-0">
                                                                <Brain className="h-4 w-4 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="truncate font-semibold text-sm text-foreground" title={item.title}>
                                                                    {truncateMemoryName(item.title)}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                                    {formatDate(item.updatedAt)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            }
                                        })}
                                    </div>
                                )}
                            </AccordionContent>
                        </AccordionItem>

                        {/* Intelligence Control Section */}
                        <AccordionItem value="intelligence-control" className="border-none">
                            <AccordionTrigger className="px-2 py-2.5 hover:no-underline rounded-md hover:bg-muted/50 transition-colors">
                                <h2 className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-2">
                                    <Sparkles className="h-3.5 w-3.5" />
                                    Intelligence Control
                                </h2>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pt-1 pb-2">
                                <button
                                    onClick={onProfileClick}
                                    className={cn(
                                        "w-full text-left px-3 py-2.5 rounded-lg transition-all",
                                        "hover:bg-accent/70 hover:text-accent-foreground",
                                        "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1",
                                        "bg-primary/5 border border-primary/20 hover:border-primary/30"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <Sparkles className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-foreground">Manage Intelligence</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                Configure assistant behavior & access
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </AccordionContent>
                        </AccordionItem>

                        {/* Analytics Section */}
                        <AccordionItem value="analytics" className="border-none">
                            <AccordionTrigger className="px-2 py-2.5 hover:no-underline rounded-md hover:bg-muted/50 transition-colors">
                                <h2 className="text-xs font-semibold text-foreground/70 uppercase tracking-wider flex items-center gap-2">
                                    <BarChart3 className="h-3.5 w-3.5" />
                                    Analytics
                                </h2>
                            </AccordionTrigger>
                            <AccordionContent className="px-2 pt-1 pb-2">
                                <button
                                    onClick={() => navigate('/analytics')}
                                    className={cn(
                                        "w-full text-left px-3 py-2.5 rounded-lg transition-all",
                                        "hover:bg-accent/70 hover:text-accent-foreground",
                                        "focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1",
                                        location.pathname === '/analytics' && "bg-accent text-accent-foreground shadow-sm"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                                            <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-foreground">Analytics</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                Value & Impact Dashboard
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </ScrollArea>

            {/* Footer - User Info (Non-clickable, informational only) */}
            <div className="px-5 py-3 border-t border-border/50">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">Muhammad Umer</div>
                            <div className="text-xs text-muted-foreground truncate">mumer@woddy.ai</div>
                        </div>
                    </div>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="flex-shrink-0 h-9 w-9"
                                aria-label="Settings"
                            >
                                <Settings className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2" align="end" side="top">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Sign Out</span>
                            </button>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Note Detail Modal */}
            <NoteDetailModal
                note={selectedNote}
                isOpen={isNoteModalOpen}
                onClose={handleNoteModalClose}
                onSave={handleNoteSave}
                availablePeople={availablePeople}
                availableGroups={availableGroups}
                availableBots={availableBots}
            />

            {/* My Notes Detail Modal */}
            {selectedMyNote && (
                <Dialog open={isMyNoteModalOpen} onOpenChange={setIsMyNoteModalOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Brain className="h-5 w-5 text-primary" />
                                {selectedMyNote.title}
                            </DialogTitle>
                            <DialogDescription>
                                Created {selectedMyNote.createdAt.toLocaleDateString()}
                                {selectedMyNote.lastUsedAt && ` • Last used ${selectedMyNote.lastUsedAt.toLocaleDateString()}`}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">
                                    Used {selectedMyNote.usageCount} time{selectedMyNote.usageCount !== 1 ? 's' : ''}
                                </Badge>
                                {selectedMyNote.category && (
                                    <Badge variant="outline" className="text-xs">
                                        {selectedMyNote.category}
                                    </Badge>
                                )}
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">Summary</h4>
                                <p className="text-sm text-muted-foreground">{selectedMyNote.summary}</p>
                            </div>

                            <Separator />

                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">Original Question</h4>
                                <p className="text-sm text-muted-foreground italic">{selectedMyNote.question}</p>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">Original Instruction</h4>
                                <p className="text-sm text-muted-foreground">{selectedMyNote.originalInstruction}</p>
                            </div>

                            <Separator />

                            {/* Access Control */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold">Access Control</h3>
                                    {!isEditingMemory ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setIsEditingMemory(true)}
                                        >
                                            <Edit2 className="h-4 w-4 mr-2" />
                                            Edit
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                // Update the note with new sharedWith
                                                const updatedNote = {
                                                    ...selectedMyNote,
                                                    sharedWith: memorySharedWith,
                                                    updatedAt: new Date(),
                                                };
                                                // Note: You may want to add an updateSystemNote function to the context
                                                setSelectedMyNote(updatedNote);
                                                setIsEditingMemory(false);
                                            }}
                                        >
                                            <Save className="h-4 w-4 mr-2" />
                                            Save
                                        </Button>
                                    )}
                                </div>

                                {/* Shared with People */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <User className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">People</span>
                                        </div>
                                        {isEditingMemory && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setShowAddMemoryPeople(!showAddMemoryPeople)}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Add
                                            </Button>
                                        )}
                                    </div>
                                    {showAddMemoryPeople && isEditingMemory && (
                                        <div className="mb-2 p-2 border rounded-md bg-muted/50">
                                            {availablePeople.filter(p => !memorySharedWith.people.includes(p.id)).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {availablePeople
                                                        .filter(p => !memorySharedWith.people.includes(p.id))
                                                        .map((person) => (
                                                            <Button
                                                                key={person.id}
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setMemorySharedWith({
                                                                        ...memorySharedWith,
                                                                        people: [...memorySharedWith.people, person.id],
                                                                    });
                                                                    setShowAddMemoryPeople(false);
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                <User className="h-3 w-3 mr-1" />
                                                                {person.name}
                                                            </Button>
                                                        ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">All available people are already added</p>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                        {memorySharedWith.people.length > 0 ? (
                                            memorySharedWith.people.map((personId) => {
                                                const person = availablePeople.find(p => p.id === personId);
                                                return (
                                                    <Badge
                                                        key={personId}
                                                        variant="secondary"
                                                        className="flex items-center gap-1"
                                                    >
                                                        <User className="h-3 w-3" />
                                                        {person?.name || personId}
                                                        {isEditingMemory && (
                                                            <button
                                                                onClick={() => setMemorySharedWith({
                                                                    ...memorySharedWith,
                                                                    people: memorySharedWith.people.filter(id => id !== personId),
                                                                })}
                                                                className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                    </Badge>
                                                );
                                            })
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Not shared with anyone</span>
                                        )}
                                    </div>
                                </div>

                                {/* Shared with Groups */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Users className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Groups</span>
                                        </div>
                                        {isEditingMemory && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setShowAddMemoryGroups(!showAddMemoryGroups)}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Add
                                            </Button>
                                        )}
                                    </div>
                                    {showAddMemoryGroups && isEditingMemory && (
                                        <div className="mb-2 p-2 border rounded-md bg-muted/50">
                                            {availableGroups.filter(g => !memorySharedWith.groups.includes(g.id)).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {availableGroups
                                                        .filter(g => !memorySharedWith.groups.includes(g.id))
                                                        .map((group) => (
                                                            <Button
                                                                key={group.id}
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setMemorySharedWith({
                                                                        ...memorySharedWith,
                                                                        groups: [...memorySharedWith.groups, group.id],
                                                                    });
                                                                    setShowAddMemoryGroups(false);
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                <Users className="h-3 w-3 mr-1" />
                                                                {group.name}
                                                            </Button>
                                                        ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">All available groups are already added</p>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                        {memorySharedWith.groups.length > 0 ? (
                                            memorySharedWith.groups.map((groupId) => {
                                                const group = availableGroups.find(g => g.id === groupId);
                                                return (
                                                    <Badge
                                                        key={groupId}
                                                        variant="secondary"
                                                        className="flex items-center gap-1"
                                                    >
                                                        <Users className="h-3 w-3" />
                                                        {group?.name || groupId}
                                                        {isEditingMemory && (
                                                            <button
                                                                onClick={() => setMemorySharedWith({
                                                                    ...memorySharedWith,
                                                                    groups: memorySharedWith.groups.filter(id => id !== groupId),
                                                                })}
                                                                className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                    </Badge>
                                                );
                                            })
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Not shared with any groups</span>
                                        )}
                                    </div>
                                </div>

                                {/* Shared with Bots */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Bot className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">Bots</span>
                                        </div>
                                        {isEditingMemory && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setShowAddMemoryBots(!showAddMemoryBots)}
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Add
                                            </Button>
                                        )}
                                    </div>
                                    {showAddMemoryBots && isEditingMemory && (
                                        <div className="mb-2 p-2 border rounded-md bg-muted/50">
                                            {availableBots.filter(b => !memorySharedWith.bots.includes(b.id)).length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {availableBots
                                                        .filter(b => !memorySharedWith.bots.includes(b.id))
                                                        .map((assistant) => (
                                                            <Button
                                                                key={assistant.id}
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setMemorySharedWith({
                                                                        ...memorySharedWith,
                                                                        bots: [...memorySharedWith.bots, assistant.id],
                                                                    });
                                                                    setShowAddMemoryBots(false);
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                <Bot className="h-3 w-3 mr-1" />
                                                                {assistant.name}
                                                            </Button>
                                                        ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">All available bots are already added</p>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                        {memorySharedWith.bots.length > 0 ? (
                                            memorySharedWith.bots.map((botId) => {
                                                const bot = availableBots.find(b => b.id === botId);
                                                return (
                                                    <Badge
                                                        key={botId}
                                                        variant="secondary"
                                                        className="flex items-center gap-1"
                                                    >
                                                        <Bot className="h-3 w-3" />
                                                        {bot?.name || botId}
                                                        {isEditingMemory && (
                                                            <button
                                                                onClick={() => setMemorySharedWith({
                                                                    ...memorySharedWith,
                                                                    bots: memorySharedWith.bots.filter(id => id !== botId),
                                                                })}
                                                                className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                    </Badge>
                                                );
                                            })
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Not shared with any bots</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    deleteSystemNote(selectedMyNote.id);
                                    setIsMyNoteModalOpen(false);
                                    setSelectedMyNote(null);
                                }}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Memory
                            </Button>
                            <Button variant="outline" onClick={() => setIsMyNoteModalOpen(false)}>
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

